import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoClose, IoSend, IoCheckmarkCircle, IoChatboxOutline } from 'react-icons/io5';
import { createFeedbackIssue } from '../../services/githubService';

const SuccessMessage = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    className="absolute inset-0 flex flex-col items-center justify-center bg-black/95 z-50 rounded-lg p-6"
  >
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", delay: 0.2 }}
      className="text-green-500 mb-4"
    >
      <IoCheckmarkCircle size={48} />
    </motion.div>
    <motion.h3
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="text-xl md:text-2xl font-bold text-white mb-2 text-center"
    >
      Thank You!
    </motion.h3>
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="text-gray-400 text-center mb-6 text-sm md:text-base"
    >
      Our team will review your feedback and use it to improve the app experience.
    </motion.p>
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClose}
      className="bg-green-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-green-600 transition-colors w-full md:w-auto"
    >
      Great!
    </motion.button>
  </motion.div>
);

const ErrorMessage = ({ message, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 20 }}
    className="fixed bottom-0 left-0 right-0 p-4 bg-red-500/90 text-white z-50"
  >
    <div className="flex items-center justify-between max-w-md mx-auto">
      <span className="text-sm md:text-base">{message}</span>
      <button
        type="button"
        onClick={onClose}
        className="text-white/80 hover:text-white ml-4"
      >
        <IoClose size={20} />
      </button>
    </div>
  </motion.div>
);

const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      const issue = await createFeedbackIssue(feedback, []);
      console.log('Feedback submitted:', issue.html_url);
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFeedback('');
        setIsOpen(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError(error.message || 'Failed to submit feedback. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="absolute bottom-16 left-0 w-[calc(100vw-32px)] md:w-[400px] bg-gradient-to-br from-black to-gray-900 border border-gray-800 rounded-2xl shadow-xl overflow-hidden"
          >
            {showSuccess && <SuccessMessage onClose={() => setShowSuccess(false)} />}
            {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
            
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 p-5 border-b border-gray-800">
              <h3 className="text-white font-bold text-lg mb-1">Help Us Improve</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                We review every suggestion. Your ideas could be in our next update!
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="relative">
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Share your experience, suggestions, or report any issues..."
                  className="w-full h-32 bg-black/50 text-white p-4 rounded-xl border border-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none placeholder-gray-500 text-base"
                  required
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full px-5 py-3.5 rounded-xl text-white font-medium transition-all flex items-center justify-center space-x-2 ${
                  isSubmitting
                    ? 'bg-blue-500/50 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg'
                }`}
              >
                <span className="text-base">{isSubmitting ? 'Sending...' : 'Submit Feedback'}</span>
                {!isSubmitting && <IoSend size={18} />}
              </motion.button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="bg-black hover:bg-gray-900 text-white px-5 py-2.5 rounded-xl shadow-lg font-medium transition-all flex items-center space-x-2 border border-gray-800"
      >
        <span className="text-sm">Feedback</span>
        <IoChatboxOutline className="text-white/90" size={16} />
      </motion.button>
    </div>
  );
};

export default FeedbackWidget; 